<script setup lang="ts">
import { MoreHorizontal } from 'lucide-vue-next'

import SidebarNav from './SidebarNav.vue'

import Error from '@/components/errors/Error.vue'
import UpsellModuleModal from '@/components/UpsellModuleModal.vue'

const router = useRouter()
const route = useRoute()
const mainRef = inject('mainRef')
const { user, merchantLogo, merchantName, isSupportConnection, setupToken } = useLocalStorage()

const isOpen = ref(false)

const mustChangePassword = computed(() => {
  if (setupToken.value?.length > 0) {
    return false
  }
  const hasToChangePassword = user.value?.hasToChangePassword ?? false
  return hasToChangePassword && !isSupportConnection.value
})

const checkOnboarding = (route) => {
  if (route !== 'Setup' && route !== 'GoCardlessFlow' && route !== 'GoCardlessFlowSuccess' && setupToken.value) {
    router.push({ name: 'Setup' })
  }
}
const passwordChanged = () => {
  user.value.hasToChangePassword = false
}

const { error, hasError } = useError()

const hasAnyError = computed(() => {
  return error.value?.module !== 'game-contest' && hasError.value
})

checkOnboarding(route.name)

const hasOverflow = ref(false)
provide('hasOverflow', {
  value: hasOverflow,
  toggle: (value: boolean) => (hasOverflow.value = value)
})

const upsellModalShown = ref<'e-reputation' | 'pongo-suite' | null>(null)

// const openUpsellModal = (type: 'e-reputation' | 'pongo-suite') => {
//   upsellModalShown.value = type
// }
</script>

<template>
  <svg width="0" height="0" aria-hidden="true" class="absolute">
    <defs>
      <linearGradient id="assistantIconGradient" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#a78bfa" />
        <stop offset="100%" stop-color="#7a2a7e" />
      </linearGradient>
    </defs>
  </svg>

  <div class="flex h-screen overflow-hidden bg-app-bg">
    <div v-show="isOpen" class="md:hidden">
      <div class="fixed inset-0 z-40 flex">
        <div class="fixed inset-0" aria-hidden="true">
          <div class="absolute inset-0 bg-foreground opacity-75" />
        </div>
        <div class="relative flex w-full max-w-xs flex-1 flex-col bg-app-bg pb-4 pt-5">
          <div class="absolute right-0 top-0 -mr-12 pt-2">
            <button
              class="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white"
              @click="isOpen = !isOpen"
            >
              <span class="sr-only">Close sidebar</span>
              <!-- Heroicon name: x -->
              <svg
                class="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="h-0 flex-1 overflow-y-auto hideScrollbar">
            <SidebarNav />
          </div>

          <!-- <UpsellModuleSidebarBlock
            class="bottom-4! w-[295px]! h-[150px]!"
            :visible="sidebarOpenedOrHovered"
            :has-subscription="subscriptions['pongo-suite'] === true"
            module="pongo-suite"
            @discover="openUpsellModal"
          /> -->
        </div>
        <div class="w-14 shrink-0" aria-hidden="true">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div class="fixed z-10 hidden h-full w-60 bg-app-bg md:flex md:shrink-0">
      <div class="relative flex w-full flex-col">
        <div class="relative flex grow flex-col overflow-y-auto pb-6 hideScrollbar">
          <div class="mt-4 flex flex-1 flex-col">
            <SidebarNav />
          </div>
        </div>

        <div class="shrink-0 p-2">
          <DropdownMenu>
            <DropdownMenuTrigger
              id="options-menu-button"
              class="group flex w-full items-center justify-between rounded-lg border border-border bg-background px-3 py-1.5 text-left outline-none transition-colors duration-200 ease-out hover:bg-nav-hover/60 focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:bg-nav-hover"
            >
              <span class="flex min-w-0 items-center gap-2.5">
                <span
                  class="inline-block size-8 shrink-0 rounded-lg bg-white bg-contain bg-center bg-no-repeat ring-1 ring-border"
                  :style="{ backgroundImage: 'url(' + merchantLogo + ')' }"
                />
                <span class="flex min-w-0 flex-col">
                  <span class="truncate text-sm font-medium text-foreground">{{ user?.name }}</span>
                  <span class="truncate text-xs text-muted-foreground">{{ merchantName }}</span>
                </span>
              </span>
              <MoreHorizontal
                class="size-4 shrink-0 text-foreground-disabled transition-colors group-hover:text-foreground-secondary group-data-[state=open]:text-foreground"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" class="w-64 overflow-visible p-0">
              <UserPanel />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>

    <div class="flex w-0 flex-1 flex-col overflow-hidden bg-transparent">
      <div class="relative z-10 flex h-16 shrink-0 border-b bg-muted md:hidden">
        <button
          class="border-r px-4 text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary lg:hidden"
          @click="isOpen = !isOpen"
        >
          <span class="sr-only">Open sidebar</span>
          <!-- Heroicon name: menu-alt-1 -->
          <svg
            class="h-6 w-6 text-foreground"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h8m-8 6h16" />
          </svg>
        </button>
        <div class="flex flex-1 items-center justify-end px-4 sm:px-6 lg:px-8">
          <DropdownMenu>
            <DropdownMenuTrigger
              id="user-menu"
              class="ml-3 flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-hidden"
            >
              <span class="sr-only">Open user menu</span>
              <span
                class="inline-block h-9 w-9 rounded-full bg-white bg-contain bg-center bg-no-repeat shadow-lg ring-2 ring-primary ring-offset-2"
                :style="{ backgroundImage: 'url(' + merchantLogo + ')' }"
                alt=""
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-64 overflow-visible p-0">
              <UserPanel />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <main
        ref="mainRef"
        :class="[
          'relative z-0 m-2 flex-1 rounded-2xl border bg-content-bg focus:outline-hidden md:ml-[15.5rem]',
          {
            'overflow-y-hidden': hasOverflow,
            'overflow-y-auto': !hasOverflow
          }
        ]"
        tabindex="0"
      >
        <div class="min-h-full">
          <Error v-if="hasAnyError" :error="error" />
          <router-view v-else />
        </div>
      </main>
    </div>
    <BaseModal :key="mustChangePassword.toString()" size="md" :is-open="mustChangePassword" :showFooter="false">
      <div class="flex flex-col items-center justify-center">
        <ChangePasswordForm @password-changed="passwordChanged" />
      </div>
    </BaseModal>

    <UpsellModuleModal :show="upsellModalShown !== null" :module="upsellModalShown" @close="upsellModalShown = null" />
  </div>
</template>
